<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Order Totals</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Imported orders from external system</p>
      </div>
    </div>

    <!-- Date Range Filter -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
        <span class="text-body-2 font-weight-medium text-medium-emphasis mr-4">Quote Date Range:</span>

        <v-menu v-model="menuStart" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(startDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="startDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <span class="text-body-2 text-medium-emphasis">to</span>

        <v-menu v-model="menuEnd" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(endDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="endDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <v-divider vertical class="mx-2 d-none d-sm-block" style="height: 24px;"></v-divider>

        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          density="compact"
          variant="outlined"
          divided
          rounded="lg"
        >
          <v-btn value="7" size="small" @click="setLastDays(7)">7 Days</v-btn>
          <v-btn value="30" size="small" @click="setLastDays(30)">30 Days</v-btn>
          <v-btn value="90" size="small" @click="setLastDays(90)">90 Days</v-btn>
          <v-btn value="all" size="small" @click="setFromBeginning">From the beginning</v-btn>
        </v-btn-toggle>
      </div>
    </v-card>

    <!-- Stats Boxes -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Total Orders</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.totalOrders }}</h3>
            </div>
            <v-avatar color="primary" variant="tonal" size="56">
              <v-icon size="28">mdi-clipboard-list-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="primary" size="18" class="mr-1">mdi-package-variant-closed</v-icon>
            <span class="text-caption text-medium-emphasis">All imported orders</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Converted from Quote</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.quoteTotal }}</h3>
            </div>
            <v-avatar color="success" variant="tonal" size="56">
              <v-icon size="28">mdi-file-document-check-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-chip size="x-small" color="success" variant="flat" class="mr-2">
              {{ stats.totalOrders > 0 ? ((stats.quoteTotal / stats.totalOrders) * 100).toFixed(1) : 0 }}%
            </v-chip>
            <span class="text-caption text-medium-emphasis">of total orders</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Quote Conversion Rate</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.totalQuotes > 0 ? ((stats.quoteTotal / stats.totalQuotes) * 100).toFixed(1) : 0 }}%</h3>
            </div>
            <v-avatar color="info" variant="tonal" size="56">
              <v-icon size="28">mdi-percent-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="info" size="18" class="mr-1">mdi-file-document-check-outline</v-icon>
            <span class="text-caption text-medium-emphasis">{{ stats.quoteTotal }} of {{ stats.totalQuotes }} quotes</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Converted from Comeback</p>
              <h3 class="text-h4 font-weight-bold">{{ stats.comebackTotal }}</h3>
            </div>
            <v-avatar color="warning" variant="tonal" size="56">
              <v-icon size="28">mdi-tag-heart-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-chip size="x-small" color="warning" variant="flat" class="mr-2">
              {{ stats.totalOrders > 0 ? ((stats.comebackTotal / stats.totalOrders) * 100).toFixed(1) : 0 }}%
            </v-chip>
            <span class="text-caption text-medium-emphasis">of total orders</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 16px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by name, email, or phone"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 400px;"
          clearable
        ></v-text-field>
        <v-select
          v-model="matchedFilter"
          :items="matchFilterOptions"
          label="Match Status"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 200px;"
          clearable
        ></v-select>
        <v-btn
          :color="fromComeback ? 'primary' : undefined"
          :variant="fromComeback ? 'flat' : 'outlined'"
          prepend-icon="mdi-tag-heart-outline"
          @click="toggleComebackFilter"
        >
          From Comeback
        </v-btn>
        <v-btn
          :color="fromQuote ? 'primary' : undefined"
          :variant="fromQuote ? 'flat' : 'outlined'"
          prepend-icon="mdi-file-document-outline"
          @click="toggleQuoteFilter"
        >
          From Quote
        </v-btn>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card>
      <v-data-table-server
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="orders"
        :items-length="total"
        :items-per-page-options="[20, 50, 100]"
        :loading="loading"
        :search="search"
        @update:options="loadItems"
      >
        <template #item.contactName="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.contactName }}</div>
            <div v-if="item.companyName" class="text-caption text-grey">{{ item.companyName }}</div>
          </div>
        </template>

        <template #item.netSale="{ item }">
          <span v-if="item.netSale != null" class="font-weight-medium">${{ formatCurrency(item.netSale) }}</span>
          <span v-else class="text-grey">--</span>
        </template>

        <template #item.grossSales="{ item }">
          <span v-if="item.grossSales != null" class="font-weight-medium">${{ formatCurrency(item.grossSales) }}</span>
          <span v-else class="text-grey">--</span>
        </template>

        <template #item.isMatched="{ item }">
          <v-chip
            :color="item.isMatched ? 'success' : 'warning'"
            size="small"
          >
            {{ item.isMatched ? 'Matched' : 'Not Matched' }}
          </v-chip>
        </template>

        <template #item.actions="{ item }">
          <v-btn
            density="compact"
            icon="mdi-eye"
            variant="text"
            @click="viewOrder(item)"
          ></v-btn>
        </template>
      </v-data-table-server>
      <div v-if="!orders.length && !loading" class="text-center py-8 text-medium-emphasis">
        No orders found.
      </div>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { importedOrderService, statisticsService } from '../services';
import { useNotificationStore } from '../stores';
import { useDebounceSearch } from '../composables/useDebounceSearch';

defineOptions({ name: 'ImportedOrdersView' });

const router = useRouter();
const notificationStore = useNotificationStore();

const orders = ref([]);
const search = ref('');
const matchedFilter = ref(null);
const fromComeback = ref(false);
const fromQuote = ref(false);
const stats = ref({ comebackTotal: 0, quoteTotal: 0, totalOrders: 0, totalQuotes: 0 });
const page = ref(1);
const itemsPerPage = ref(20);
const total = ref(0);
const loading = ref(false);
const startDate = ref(new Date(new Date().getTime() - 29 * 24 * 60 * 60 * 1000));
const endDate = ref(new Date());
const selectedPeriod = ref('30');
const menuStart = ref(false);
const menuEnd = ref(false);

function toYmd(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDisplay(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function onDateChange() {
  if (startDate.value && endDate.value && startDate.value > endDate.value) {
    endDate.value = startDate.value;
  }
  loadQuoteCount();
}

function setLastDays(days) {
  const end = new Date();
  const start = new Date(end.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  startDate.value = start;
  endDate.value = end;
  selectedPeriod.value = String(days);
  loadQuoteCount();
}

function setFromBeginning() {
  startDate.value = new Date(2025, 7, 18); // August 18th 2025
  endDate.value = new Date();
  selectedPeriod.value = 'all';
  loadQuoteCount();
}

async function loadQuoteCount() {
  try {
    const response = await statisticsService.getDashboardSummary({
      startIso: toYmd(startDate.value),
      endIso: toYmd(endDate.value),
    });
    if (response.success) {
      stats.value.totalQuotes = response.data.totalQuotes;
    }
  } catch {
    // keep existing totalQuotes on error
  }
}

const matchFilterOptions = [
  { title: 'Matched', value: 'true' },
  { title: 'Not Matched', value: 'false' },
];

const headers = [
  { title: 'Contact Name', key: 'contactName' },
  { title: 'Email', key: 'email', sortable: false },
  { title: 'Phone', key: 'phone', sortable: false },
  { title: 'Event Date', key: 'eventDate', sortable: false },
  { title: 'Net Sale', key: 'netSale', sortable: false, align: 'end' },
  { title: 'Gross Sales', key: 'grossSales', sortable: false, align: 'end' },
  { title: 'Status', key: 'isMatched', sortable: false },
  { title: '', key: 'actions', sortable: false, width: '50px' },
];

async function loadOrders(targetPage = 1, pageSize = 20) {
  loading.value = true;
  try {
    const params = {
      page: targetPage,
      limit: pageSize,
      search: search.value,
    };
    if (matchedFilter.value !== null && matchedFilter.value !== undefined) {
      params.matched = matchedFilter.value;
    }
    if (fromComeback.value) {
      params.fromComeback = 'true';
    }
    if (fromQuote.value) {
      params.fromQuote = 'true';
    }
    const data = await importedOrderService.list(params);
    orders.value = data.data;
    total.value = data.total;
    page.value = data.page;
    itemsPerPage.value = pageSize;
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load orders');
  } finally {
    loading.value = false;
  }
}

useDebounceSearch(search, () => {
  page.value = 1;
  loadOrders(1, itemsPerPage.value);
});

watch(matchedFilter, () => {
  page.value = 1;
  loadOrders(1, itemsPerPage.value);
});

function toggleComebackFilter() {
  fromComeback.value = !fromComeback.value;
  page.value = 1;
  loadOrders(1, itemsPerPage.value);
}

function toggleQuoteFilter() {
  fromQuote.value = !fromQuote.value;
  page.value = 1;
  loadOrders(1, itemsPerPage.value);
}

async function loadStats() {
  try {
    const data = await importedOrderService.getStats();
    stats.value = data;
  } catch (err) {
    // silently ignore stats errors
  }
}

function loadItems(options) {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  loadOrders(page.value, itemsPerPage.value);
}

function viewOrder(order) {
  router.push({ name: 'ImportedOrderDetail', params: { id: order.id } });
}

onMounted(() => {
  loadStats();
  loadQuoteCount();
});

function formatCurrency(amount) {
  if (amount == null) return '0.00';
  return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
</script>

<style scoped>
:deep(.v-data-table) {
  border-radius: 0 0 12px 12px;
}

:deep(.v-data-table-header) {
  background-color: #f8fafc;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: #64748b !important;
}
</style>
